// src/components/Spreadsheet/SpreadsheetWorkspaceSidePanel.jsx

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';

import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@hello-pangea/dnd';

import { safeGetItem, safeSetItem } from '../../utils/storageUtils';

/**
 * SpreadsheetWorkspaceSidePanel
 * -----------------------------------------------------------------------------
 * Reusable sidepanel shell for spreadsheet-based workspaces.
 *
 * DESIGN GOAL
 *  • Match the SmartListsSidePanel shell design:
 *      - same topbar background
 *      - same border treatment
 *      - same collapse/expand icon style
 *      - same collapsed mini-rail behavior
 *      - same DragDropContext/Droppable/Draggable row-reorder pattern
 *
 * PURPOSE
 *  • Keep the shell reusable for FormListsLab, SmartLists migration, and future
 *    spreadsheet workspaces.
 *  • Let the parent provide list-mode sections and settings-mode content.
 *
 * IMPORTANT
 *  • This component does NOT know about:
 *      - fieldsMetadata
 *      - dropdownOptions
 *      - dropdownDependencies
 *      - Form Lists
 *      - SmartLists
 *      - FieldSettings internals
 *      - Firestore
 *      - save/publish
 *
 * ENTERPRISE RULE
 *  • The panel owns only workspace chrome and drag/drop row movement events.
 *  • The parent owns all data, persistence, settings behavior, and ordering.
 */

const TOPBAR_BG = 'rgba(213, 228, 249, 0.5)';
const TOPBAR_HEIGHT = 50;
const TOPBAR_ICON_COLOR = '#003459';
const SIDEPANEL_BORDER = '1px solid #e0e0e0';
const DIRTY_BADGE_SIZE = 8;
const DIRTY_BADGE_COLOR = '#d97706';
const DIRTY_BADGE_BOX_SHADOW = '0 0 0 2px rgba(255,255,255,0.9)';

const SpreadsheetWorkspaceSidePanel = memo(({
  mode = 'list',
  title = 'Lists',
  sections = [],
  settingsContent = null,
  topBar = null,
  width = '30%',
  minWidth = '30%',
  maxWidth = '30%',
  collapsedWidth = 56,
  storageKey = '',
  isCollapsed: controlledIsCollapsed = undefined,
  onToggleCollapse = null,
  dirtyBadgeVisible = false,
  collapsedDirtyBadgeVisible = false,
  onRowReorder = null,
  hideTopBarInSettings = true,
}) => {
  const [expanded, setExpanded] = useState({});
  const [hasHydratedExpanded, setHasHydratedExpanded] = useState(false);

  /**
   * Drag-end duplicate guard
   * ---------------------------------------------------------------------------
   * react-beautiful-dnd can emit duplicate drag-end style events in development
   * under React StrictMode / hot reload conditions.
   *
   * WHY THIS EXISTS
   *  • A duplicate reorder event can make the same drag feel delayed or can
   *    reorder against an already-updated list.
   *  • SmartLists works because the parent state is lightweight; FormListsLab
   *    also updates a large spreadsheet canvas, so duplicate events are much
   *    more noticeable.
   *
   * IMPORTANT
   *  • This guard is scoped to identical source/destination signatures only.
   *  • It does not block legitimate subsequent drags.
   */
  const lastDragEndSignatureRef = useRef({
    signature: '',
    timestamp: 0,
  });

  // ---------------------------------------------------------------------------
  // Collapse state: controlled or uncontrolled
  // ---------------------------------------------------------------------------
  const [uncontrolledIsCollapsed, setUncontrolledIsCollapsed] = useState(false);

  const isCollapsed =
    typeof controlledIsCollapsed === 'boolean'
      ? controlledIsCollapsed
      : uncontrolledIsCollapsed;

  /**
   * Dirty badge visibility
   * ---------------------------------------------------------------------------
   * Prefer the generic dirtyBadgeVisible prop for reusable spreadsheet
   * workspaces. Keep collapsedDirtyBadgeVisible as a backward-compatible alias
   * so existing callers do not break while they migrate.
   */
  const effectiveDirtyBadgeVisible =
    dirtyBadgeVisible === true ||
    collapsedDirtyBadgeVisible === true;

  const handleToggleCollapse = useCallback(() => {
    const nextCollapsed = !isCollapsed;

    if (typeof onToggleCollapse === 'function') {
      onToggleCollapse(nextCollapsed);
      return;
    }

    setUncontrolledIsCollapsed(nextCollapsed);
  }, [
    isCollapsed,
    onToggleCollapse,
  ]);

  // ---------------------------------------------------------------------------
  // Accordion persistence for list mode
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (mode !== 'list') return;
    if (!storageKey) return;

    if (!Array.isArray(sections) || sections.length === 0) {
      return;
    }

    const currentPanelKeys = [];
    sections.forEach((section, index) => {
      if (!section?.noAccordion) {
        currentPanelKeys.push(`panel${index}`);
      }
    });

    const saved = safeGetItem(storageKey);

    const savedMap =
      saved && typeof saved === 'object' && !Array.isArray(saved)
        ? saved
        : null;

    const nextExpanded = savedMap ? { ...savedMap } : {};

    currentPanelKeys.forEach((panelKey) => {
      if (typeof nextExpanded[panelKey] !== 'boolean') {
        nextExpanded[panelKey] = true;
      }
    });

    Object.keys(nextExpanded).forEach((panelKey) => {
      if (!currentPanelKeys.includes(panelKey)) {
        delete nextExpanded[panelKey];
      }
    });

    setExpanded(nextExpanded);
    setHasHydratedExpanded(true);
  }, [
    mode,
    sections,
    storageKey,
  ]);

  useEffect(() => {
    if (mode !== 'list') return;
    if (!storageKey) return;
    if (!hasHydratedExpanded) return;

    safeSetItem(storageKey, expanded);
  }, [
    mode,
    expanded,
    storageKey,
    hasHydratedExpanded,
  ]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({
      ...prev,
      [panel]: isExpanded ? true : false,
    }));
  };

  // ---------------------------------------------------------------------------
  // Drag-and-drop handler
  // ---------------------------------------------------------------------------
  const handleDragEnd = useCallback((result, sectionIndex) => {
    const { source, destination } = result;

    if (!destination) return;

    const dragSignature = JSON.stringify({
      sectionIndex,
      sourceDroppableId: source?.droppableId || '',
      sourceIndex: source?.index,
      destinationDroppableId: destination?.droppableId || '',
      destinationIndex: destination?.index,
    });

    const now = Date.now();
    const previous = lastDragEndSignatureRef.current;

    if (
      previous.signature === dragSignature &&
      now - previous.timestamp < 250
    ) {
      console.log('[SpreadsheetWorkspaceSidePanel] Ignoring duplicate drag end:', {
        sectionIndex,
        source,
        destination,
      });
      return;
    }

    lastDragEndSignatureRef.current = {
      signature: dragSignature,
      timestamp: now,
    };

    console.log('[SpreadsheetWorkspaceSidePanel] Drag ended:', {
      sectionIndex,
      source,
      destination,
    });

    if (typeof onRowReorder === 'function') {
      onRowReorder({
        sectionIndex,
        sourceIndex: source.index,
        destinationIndex: destination.index,
      });
    }
  }, [onRowReorder]);

  const defaultTopBar = useMemo(() => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        width: '100%',
        height: '100%',
        px: 1.25,
        boxSizing: 'border-box',
      }}
    >
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontWeight: 700,
          color: TOPBAR_ICON_COLOR,
          fontSize: '0.8rem',
        }}
      >
        {title}
      </Typography>
    </Box>
  ), [title]);

  // ---------------------------------------------------------------------------
  // Collapsed rail UI
  // ---------------------------------------------------------------------------
  if (isCollapsed) {
    return (
      <Box
        sx={{
          minWidth: `${collapsedWidth}px`,
          maxWidth: `${collapsedWidth}px`,
          width: `${collapsedWidth}px`,
          bgcolor: TOPBAR_BG,
          position: 'relative',
          height: '100%',
          minHeight: 0,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflow: 'hidden',
          borderRight: SIDEPANEL_BORDER,
          boxSizing: 'border-box',
        }}
      >
        {effectiveDirtyBadgeVisible ? (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: DIRTY_BADGE_SIZE,
              height: DIRTY_BADGE_SIZE,
              borderRadius: '50%',
              backgroundColor: DIRTY_BADGE_COLOR,
              boxShadow: DIRTY_BADGE_BOX_SHADOW,
              zIndex: 2,
            }}
          />
        ) : null}

        <Box
          sx={{
            minHeight: `${TOPBAR_HEIGHT}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 0.75,
            pb: 0.5,
            backgroundColor: TOPBAR_BG,
            gap: 0.5,
          }}
        >
          <Tooltip title="Expand sidebar" arrow placement="right">
            <IconButton
              onClick={handleToggleCollapse}
              aria-label="Expand sidebar"
              sx={{
                color: TOPBAR_ICON_COLOR,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <KeyboardDoubleArrowRightIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            backgroundColor: TOPBAR_BG,
          }}
        />
      </Box>
    );
  }

  const shouldRenderTopBar =
    !(mode === 'settings' && hideTopBarInSettings === true);

  return (
    <Box
      sx={{
        minWidth,
        maxWidth,
        width,
        bgcolor: 'background.paper',
        height: '100%',
        minHeight: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflow: 'hidden',
        borderRight: SIDEPANEL_BORDER,
        boxSizing: 'border-box',
      }}
    >
      {shouldRenderTopBar ? (
        <Box
          sx={{
            minHeight: `${TOPBAR_HEIGHT}px`,
            height: `${TOPBAR_HEIGHT}px`,
            backgroundColor: TOPBAR_BG,
            borderBottom: SIDEPANEL_BORDER,
            display: 'flex',
            alignItems: 'stretch',
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'stretch',
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
              '& > *': {
                flex: 1,
                minWidth: 0,
                backgroundColor: 'transparent !important',
                borderBottom: 'none !important',
                boxSizing: 'border-box',
              },
            }}
          >
            {topBar || defaultTopBar}
          </Box>

          <Box
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              pr: 0.5,
              pl: 0,
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
            }}
          >
            {effectiveDirtyBadgeVisible ? (
              <Tooltip title="Unsaved Lists changes" arrow placement="left">
                <Box
                  aria-label="Unsaved Lists changes"
                  sx={{
                    width: DIRTY_BADGE_SIZE,
                    height: DIRTY_BADGE_SIZE,
                    borderRadius: '50%',
                    backgroundColor: DIRTY_BADGE_COLOR,
                    boxShadow: DIRTY_BADGE_BOX_SHADOW,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            ) : null}

            <Tooltip title="Collapse sidebar" arrow placement="left">
              <IconButton
                onClick={handleToggleCollapse}
                aria-label="Collapse sidebar"
                sx={{
                  color: TOPBAR_ICON_COLOR,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <KeyboardDoubleArrowLeftIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : null}

      {mode === 'settings' ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            pb: 2,
          }}
        >
          {settingsContent || (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select a field to edit.
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            pb: 2,
          }}
        >
          {sections.map((section, index) => {
            if (section.noAccordion) {
              return (
                <Box key={index} sx={{ p: section.noPadding ? 0 : 2 }}>
                  <DragDropContext
                    onDragEnd={(result) => handleDragEnd(result, index)}
                  >
                    <Droppable droppableId={`spreadsheet-section-${index}`}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {section.items.map((item, idx) => (
                            <Draggable
                              key={item.localDraftId || idx}
                              draggableId={item.localDraftId || `idx-${idx}`}
                              index={idx}
                            >
                              {(draggableProvided, snapshot) => (
                                <Box
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  sx={{
                                    width: '100%',
                                    m: 0,
                                    p: section.noPadding ? 0 : '4px 8px',
                                    borderBottom: '0px solid #e0e0e0',
                                    opacity: snapshot.isDragging ? 0.82 : 1,
                                    backgroundColor: snapshot.isDragging
                                      ? 'rgba(25, 118, 210, 0.08)'
                                      : 'transparent',
                                    boxSizing: 'border-box',
                                  }}
                                >
                                  {item.element}
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Box>
              );
            }

            return null;
          })}
        </Box>
      )}
    </Box>
  );
});

SpreadsheetWorkspaceSidePanel.displayName = 'SpreadsheetWorkspaceSidePanel';

SpreadsheetWorkspaceSidePanel.propTypes = {
  mode: PropTypes.oneOf(['list', 'settings']),
  title: PropTypes.string,
  sections: PropTypes.arrayOf(PropTypes.object),
  settingsContent: PropTypes.node,
  topBar: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  collapsedWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  storageKey: PropTypes.string,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  dirtyBadgeVisible: PropTypes.bool,
  collapsedDirtyBadgeVisible: PropTypes.bool,
  onRowReorder: PropTypes.func,
  hideTopBarInSettings: PropTypes.bool,
};

export default SpreadsheetWorkspaceSidePanel;